import { asSnapshot, visibleAnswers } from '@lib/customization/poiRules';
import { stepNoticeType } from '@lib/spproject/inspectionStatus';
import { type Inspection } from '@lib/spproject/types';
import {
  useCustomizationLocales, type ResolvedCustomizationLocales,
} from '@lib/customization/useCustomizationLocales';
import { resolveLabel, resolveLabelUpper, type PoiField } from '@store/api/poiTypeEndpoints';
import { Fragment, useMemo, type FC, type ReactNode } from 'react';
import { CheckIcon, ClipboardListIcon, TimesIcon } from 'react-line-awesome';
import {
  CustomFieldGroupTitle, CustomFieldLabel, CustomFieldsContent, CustomFieldSection, CustomFieldsGrid,
  CustomFieldsHeading, CustomFieldsBody, CustomFieldsRow, CustomFieldValue, IconWrapper,
} from './InspectionDetail.styled';

/** What an unanswered field shows — the same dash the rows above use for a missing value. */
const EMPTY = '-';

/**
 * A number as the reader writes it — `42,5` in Turkish — but never grouped: a year installed
 * of `2000` is not `2.000`. Every fraction digit is kept; rounding a recorded measurement for
 * display would misreport it.
 */
const formatNumber = (value: number, locale: string) =>
  value.toLocaleString(locale, { useGrouping: false, maximumFractionDigits: 20 });

/**
 * One answer, shaped by its field's type. Anything that is not the shape its type promises
 * reads as unanswered — the snapshot is stored unvalidated, so this cannot assume it.
 */
const formatAnswer = (field: PoiField, locales: ResolvedCustomizationLocales): ReactNode => {
  const { value } = field;

  const optionName = (id: string) => {
    const option = (field.options ?? []).find((candidate) => candidate?.id === id);

    return option ? resolveLabel(option.label, locales) || id : id;
  };

  switch (field.type) {
    case 'Toggle':
      if (value === true) return <CheckIcon />;
      if (value === false) return <TimesIcon />;
      return EMPTY;

    case 'List':
      return typeof value === 'string' && value ? optionName(value) : EMPTY;

    case 'MultiList':
      return Array.isArray(value) && value.length ? value.map(optionName).join(', ') : EMPTY;

    case 'Number':
    case 'Range':
      return typeof value === 'number' && Number.isFinite(value) ? formatNumber(value, locales.preferred) : EMPTY;

    default:
      return typeof value === 'string' && value ? value : EMPTY;
  }
};

interface Props {
  /**
   * The step whose answers these are. Its `customFields` is as stored — narrowed here, never
   * trusted — and its status is what a rule on the step's status asks about: this step's own,
   * not the point's.
   */
  step: Pick<Inspection.Step, 'customFields' | 'noticeType'> | undefined;
}

/**
 * The inspector's answers to the POI type attached to this step.
 *
 * Only what the inspector was actually shown: the snapshot's own rules are evaluated against
 * its own answers and the step's status, and a field they hid — or one in a group they hid —
 * is left out. A field whose `showOnReports` is off is still shown; this panel is not a report.
 *
 * Headed by the type's own name, which is data rather than chrome, so the section needs no
 * string of its own.
 */
const PoiCustomFields: FC<Props> = ({ step }) => {
  const locales = useCustomizationLocales();

  const status = stepNoticeType(step);
  const snapshot = useMemo(() => asSnapshot(step?.customFields), [step?.customFields]);
  const sections = useMemo(() => (snapshot ? visibleAnswers(snapshot, status) : []), [snapshot, status]);

  if (!snapshot || sections.length === 0) return null;

  return (
    <CustomFieldsRow>
      <IconWrapper>
        <ClipboardListIcon style={{ fill: '#9e9e9e' }} />
      </IconWrapper>
      <CustomFieldsContent>
        <CustomFieldsHeading>{resolveLabel(snapshot.name, locales)}</CustomFieldsHeading>
        <CustomFieldsBody>
          {sections.map((section, sectionIndex) => (
            <CustomFieldSection key={section.group?.id ?? `ungrouped-${sectionIndex}`}>
              {section.group && (
                <CustomFieldGroupTitle>{resolveLabelUpper(section.group.name, locales)}</CustomFieldGroupTitle>
              )}
              <CustomFieldsGrid $grouped={Boolean(section.group)}>
                {section.fields.map((field, fieldIndex) => (
                  <Fragment key={field.id ?? fieldIndex}>
                    <CustomFieldLabel>{resolveLabel(field.label, locales) || field.id}</CustomFieldLabel>
                    <CustomFieldValue>{formatAnswer(field, locales)}</CustomFieldValue>
                  </Fragment>
                ))}
              </CustomFieldsGrid>
            </CustomFieldSection>
          ))}
        </CustomFieldsBody>
      </CustomFieldsContent>
    </CustomFieldsRow>
  );
};

export default PoiCustomFields;
